// utils/ProfitAndLossReport.js
import React, { useEffect } from "react";

const formatAmount = (value) => {
  const num = Number(value) || 0;
  const formatted = Math.abs(num).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return num < 0 ? `(${formatted})` : formatted;
};

const formatAccountName = (name) => {
  if (!name) return "";
  return name
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const ProfitAndLossReport = ({
  data,
  companyName = "Your Company Name",
  reportTitle = "Profit & Loss Statement",
  periodLabel = "",
  preparedBy = "",
  onAfterPrint, // ← added here
}) => {
  // Auto-print when component mounts
  useEffect(() => {
    if (!data) return;

    const timer = setTimeout(() => {
      window.print();
    }, 300);

    return () => clearTimeout(timer);
  }, [data]);

  // Return to previous view after print dialog closes
  useEffect(() => {
    const handleAfterPrint = () => {
      if (typeof onAfterPrint === "function") {
        onAfterPrint();
      }
    };

    window.addEventListener("afterprint", handleAfterPrint);
    return () => window.removeEventListener("afterprint", handleAfterPrint);
  }, [onAfterPrint]);

  if (!data) return null;

  return (
    <div className="pl-report">
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 12mm 15mm;
          }
          body * {
            visibility: hidden;
          }
          .pl-report, .pl-report * {
            visibility: visible;
          }
          .pl-report {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            font-size: 11pt;
          }
          .no-print {
            display: none !important;
          }
        }

        .pl-report {
          font-family: "Segoe UI", Arial, Helvetica, sans-serif;
          color: #111;
          max-width: 210mm;
          margin: 0 auto;
          padding: 20px 24px;
          background: #fff;
          line-height: 1.35;
        }

        .pl-header {
          text-align: center;
          margin-bottom: 22px;
          border-bottom: 2px solid #222;
          padding-bottom: 12px;
        }

        .pl-header h1 {
          margin: 0 0 4px 0;
          font-size: 18pt;
          font-weight: 700;
          letter-spacing: 0.3px;
        }

        .pl-header h2 {
          margin: 0 0 6px 0;
          font-size: 13pt;
          font-weight: 600;
          color: #333;
        }

        .pl-header .period {
          font-size: 10pt;
          color: #555;
        }

        .pl-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 6px;
        }

        .pl-table td {
          padding: 5px 6px;
          vertical-align: top;
        }

        .pl-table .account {
          padding-left: 18px;
        }

        .pl-table .amount {
          text-align: right;
          white-space: nowrap;
          font-variant-numeric: tabular-nums;
          width: 140px;
        }

        .pl-table .section-title {
          font-weight: 700;
          font-size: 11pt;
          padding-top: 14px;
          padding-bottom: 4px;
          border-bottom: 1px solid #999;
        }

        .pl-table .total-row td {
          font-weight: 700;
          border-top: 1px solid #333;
          padding-top: 6px;
        }

        .pl-table .gross-row td,
        .pl-table .net-row td {
          font-weight: 700;
          font-size: 11.5pt;
          border-top: 2px solid #111;
          border-bottom: 2px solid #111;
          padding-top: 8px;
          padding-bottom: 8px;
        }

        .pl-table .negative {
          color: #b91c1c;
        }

        .pl-footer {
          margin-top: 36px;
          font-size: 9pt;
          color: #555;
          display: flex;
          justify-content: space-between;
        }
      `}</style>

      {/* REPORT HEADER */}
      <div className="pl-header">
        <h1>{companyName}</h1>
        <h2>{reportTitle}</h2>
        {periodLabel && <div className="period">{periodLabel}</div>}
      </div>

      {/* REPORT BODY */}
      <table className="pl-table">
        <tbody>
          {/* REVENUE */}
          <tr>
            <td className="section-title" colSpan={2}>
              Revenue
            </td>
          </tr>
          {(data.revenueRows || []).map((row) => (
            <tr key={row.accountId}>
              <td className="account">{formatAccountName(row.accountName)}</td>
              <td className="amount">{formatAmount(row.amount)}</td>
            </tr>
          ))}
          <tr className="total-row">
            <td>Total Revenue</td>
            <td className="amount">{formatAmount(data.totalRevenue)}</td>
          </tr>

          {/* COST OF GOODS SOLD */}
          <tr>
            <td className="section-title" colSpan={2}>
              Cost of Goods Sold
            </td>
          </tr>
          {(data.cogsRows || []).map((row) => (
            <tr key={row.accountId}>
              <td className="account">{formatAccountName(row.accountName)}</td>
              <td className="amount">{formatAmount(row.amount)}</td>
            </tr>
          ))}
          <tr className="total-row">
            <td>Total Cost of Goods Sold</td>
            <td className="amount">{formatAmount(data.totalCogs)}</td>
          </tr>

          {/* GROSS PROFIT */}
          <tr className="gross-row">
            <td>Gross Profit</td>
            <td className={`amount ${data.grossProfit < 0 ? "negative" : ""}`}>
              {formatAmount(data.grossProfit)}
            </td>
          </tr>

          {/* OPERATING EXPENSES */}
          <tr>
            <td className="section-title" colSpan={2}>
              Operating Expenses
            </td>
          </tr>
          {(data.expenseRows || []).map((row) => (
            <tr key={row.accountId}>
              <td className="account">{formatAccountName(row.accountName)}</td>
              <td className="amount">{formatAmount(row.amount)}</td>
            </tr>
          ))}
          <tr className="total-row">
            <td>Total Operating Expenses</td>
            <td className="amount">{formatAmount(data.totalExpenses)}</td>
          </tr>

          {/* NET INCOME */}
          <tr className="net-row">
            <td>Net Income / (Loss)</td>
            <td className={`amount ${data.netIncome < 0 ? "negative" : ""}`}>
              {formatAmount(data.netIncome)}
            </td>
          </tr>
        </tbody>
      </table>

      {/* FOOTER */}
      <div className="pl-footer">
        <div>
          {preparedBy && <div>Prepared by: {preparedBy}</div>}
          <div>Generated on: {new Date().toLocaleDateString("en-GB")}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div>Page 1 of 1</div>
        </div>
      </div>
    </div>
  );
};

export default ProfitAndLossReport;
